import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { generateAPI, videosAPI, BASE_URL } from "../lib/api";
import { useAuthStore } from "../lib/store";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { ScrollArea } from "./ui/scroll-area";
import StoryLibraryPanel from "./StoryLibraryPanel";
import StoryEditor from "./StoryEditor";
import {
  Sparkles,
  Loader2,
  Plus,
  X,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  User,
  Image as ImageIcon,
  Film,
  Wand2,
  Play,
  Pause,
  Download,
  RefreshCw,
  Upload,
  Lock,
  Unlock,
  BookOpen,
  Users,
  Palette,
  Settings,
  Check,
  AlertCircle,
  Volume2,
  VolumeX,
  Mic,
  History,
} from "lucide-react";

// Maximum limits
const MAX_SCENES = 10;
const MAX_CHARACTERS = 3;

// Default scene template
const createDefaultScene = (index) => ({
  id: `scene-${Date.now()}-${index}`,
  title: `Scene ${index + 1}`,
  narrative: "",
  visualPrompt: "", // AI-generated or manual
  duration: 5, // seconds
  environment: "",
  characterIds: [], // References to characters in this scene
  isLocked: false,
  status: "pending", // pending, generating, complete, error
  videoUrl: null,
  thumbnailUrl: null,
});

// Default character template
const createDefaultCharacter = (index) => ({
  id: `char-${Date.now()}-${index}`,
  name: `Character ${index + 1}`,
  description: "",
  referenceImageUrl: null,
  referenceImagePreview: null,
  isAutoGenerated: false,
  color: ["#00F0FF", "#A855F7", "#F59E0B"][index % 3], // Assign color for UI
});

export default function StoryModePanel({ onVideoCreated }) {
  const navigate = useNavigate();
  
  // Story state
  const [storyTitle, setStoryTitle] = useState("");
  const [storyNarrative, setStoryNarrative] = useState("");
  const [scenes, setScenes] = useState([createDefaultScene(0)]);
  const [characters, setCharacters] = useState([]);
  const [globalEnvironment, setGlobalEnvironment] = useState("");
  const [globalStyle, setGlobalStyle] = useState("cinematic"); // cinematic, anime, realistic
  
  // Narration state
  const [enableNarration, setEnableNarration] = useState(true);
  const [useNativeSpeech, setUseNativeSpeech] = useState(false); // NEW: Use H3's native speech instead of TTS overlay
  const [narratorVoice, setNarratorVoice] = useState("female"); // male, female, auto
  const [narratorStyle, setNarratorStyle] = useState("storyteller"); // storyteller, dramatic, calm, energetic
  const [narratorSpeed, setNarratorSpeed] = useState("normal"); // slow, normal, fast
  
  // Background music state
  const [enableBackgroundMusic, setEnableBackgroundMusic] = useState(true);
  const [musicMood, setMusicMood] = useState("cinematic"); // cinematic, uplifting, suspenseful, romantic, adventure, calm
  const [musicVolume, setMusicVolume] = useState(30); // 0-100, default 30% (underneath narration)
  
  // Video format state
  const [aspectRatio, setAspectRatio] = useState("9:16"); // "9:16" (TikTok), "16:9" (YouTube), "1:1" (Square), "4:5" (IG Feed)
  
  // UI state
  const [activeTab, setActiveTab] = useState("story"); // story, characters, scenes, generate, library
  const [expandedSceneId, setExpandedSceneId] = useState(null);
  const [isParsingStory, setIsParsingStory] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStatus, setGenerationStatus] = useState("");
  const [currentGeneratingScene, setCurrentGeneratingScene] = useState(null);
  
  // Story editing state (for Previous Stories)
  const [editingStory, setEditingStory] = useState(null);
  
  // Character image upload refs
  const characterImageRefs = useRef({});
  const [uploadingCharacterId, setUploadingCharacterId] = useState(null);

  // ===== STORY PARSING =====
  const handleParseStory = async () => {
    if (!storyNarrative.trim()) {
      toast.error("Please enter your story first");
      return;
    }

    setIsParsingStory(true);
    try {
      const token = useAuthStore.getState().token;
      const response = await fetch(`${BASE_URL}/api/story/parse`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          narrative: storyNarrative,
          maxScenes: MAX_SCENES,
          maxCharacters: MAX_CHARACTERS,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to parse story");
      }

      const data = await response.json();
      
      if (data.scenes && data.scenes.length > 0) {
        setScenes(data.scenes.map((scene, index) => ({
          ...createDefaultScene(index),
          ...scene,
          id: `scene-${Date.now()}-${index}`,
        })));
        toast.success(`Story parsed into ${data.scenes.length} scenes!`);
      }

      if (data.characters && data.characters.length > 0) {
        setCharacters(data.characters.slice(0, MAX_CHARACTERS).map((char, index) => ({
          ...createDefaultCharacter(index),
          ...char,
          id: `char-${Date.now()}-${index}`,
        })));
        toast.success(`Found ${Math.min(data.characters.length, MAX_CHARACTERS)} characters`);
      }

      if (data.title) {
        setStoryTitle(data.title);
      }

      if (data.globalEnvironment) {
        setGlobalEnvironment(data.globalEnvironment);
      }

      // Move to scenes tab
      setActiveTab("scenes");
    } catch (error) {
      console.error("Story parsing error:", error);
      toast.error("Failed to parse story. Please try again.");
    } finally {
      setIsParsingStory(false);
    }
  };

  // ===== SCENE MANAGEMENT =====
  const addScene = () => {
    if (scenes.length >= MAX_SCENES) {
      toast.error(`Maximum ${MAX_SCENES} scenes allowed`);
      return;
    }
    const newScene = createDefaultScene(scenes.length);
    setScenes([...scenes, newScene]);
    setExpandedSceneId(newScene.id);
  };

  const removeScene = (sceneId) => {
    if (scenes.length <= 1) {
      toast.error("Story must have at least one scene");
      return;
    }
    setScenes(scenes.filter((s) => s.id !== sceneId));
    if (expandedSceneId === sceneId) {
      setExpandedSceneId(null);
    }
  };

  const updateScene = (sceneId, updates) => {
    setScenes(scenes.map((s) => (s.id === sceneId ? { ...s, ...updates } : s)));
  };

  const moveScene = (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= scenes.length) return;
    
    const newScenes = [...scenes];
    [newScenes[index], newScenes[newIndex]] = [newScenes[newIndex], newScenes[index]];
    setScenes(newScenes);
  };

  // ===== CHARACTER MANAGEMENT =====
  const addCharacter = () => {
    if (characters.length >= MAX_CHARACTERS) {
      toast.error(`Maximum ${MAX_CHARACTERS} characters allowed`);
      return;
    }
    setCharacters([...characters, createDefaultCharacter(characters.length)]);
  };

  const removeCharacter = (charId) => {
    setCharacters(characters.filter((c) => c.id !== charId));
    // Remove from all scenes
    setScenes(scenes.map((s) => ({
      ...s,
      characterIds: s.characterIds.filter((id) => id !== charId),
    })));
  };

  const updateCharacter = (charId, updates) => {
    setCharacters(characters.map((c) => (c.id === charId ? { ...c, ...updates } : c)));
  };

  // Handle character reference image upload
  const handleCharacterImageUpload = async (charId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload JPG, PNG, or WebP image");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }

    setUploadingCharacterId(charId);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = useAuthStore.getState().token;
      const response = await fetch(`${BASE_URL}/api/upload/image`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData,
      });

      const result = await response.json();
      if (result.success) {
        const previewUrl = URL.createObjectURL(file);
        updateCharacter(charId, {
          referenceImageUrl: result.image_url,
          referenceImagePreview: previewUrl,
          isAutoGenerated: false,
        });
        toast.success("Character image uploaded!");
      } else {
        throw new Error(result.detail || "Upload failed");
      }
    } catch (error) {
      console.error("Character image upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploadingCharacterId(null);
    }
  };

  // Auto-generate character reference
  const generateCharacterReference = async (charId) => {
    const character = characters.find((c) => c.id === charId);
    if (!character || !character.description.trim()) {
      toast.error("Please add a character description first");
      return;
    }

    setUploadingCharacterId(charId);
    try {
      const token = useAuthStore.getState().token;
      const response = await fetch(`${BASE_URL}/api/characters/generate-preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          description: character.description,
          name: character.name,
          style: globalStyle,
        }),
      });

      const result = await response.json();
      if (result.success && result.image_url) {
        updateCharacter(charId, {
          referenceImageUrl: result.image_url,
          referenceImagePreview: result.image_url,
          isAutoGenerated: true,
        });
        toast.success("Character reference generated!");
      } else {
        throw new Error(result.error || "Generation failed");
      }
    } catch (error) {
      console.error("Character generation error:", error);
      toast.error("Failed to generate character reference");
    } finally {
      setUploadingCharacterId(null);
    }
  };

  // Toggle character in scene
  const toggleCharacterInScene = (sceneId, charId) => {
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;

    const hasChar = scene.characterIds.includes(charId);
    updateScene(sceneId, {
      characterIds: hasChar
        ? scene.characterIds.filter((id) => id !== charId)
        : [...scene.characterIds, charId],
    });
  };

  // ===== VIDEO GENERATION =====
  const handleGenerateStory = async () => {
    // Validation
    if (scenes.length === 0) {
      toast.error("Add at least one scene");
      return;
    }

    const emptyScenes = scenes.filter((s) => !s.narrative.trim() && !s.visualPrompt.trim());
    if (emptyScenes.length > 0) {
      toast.error("All scenes need content (narrative or visual description)");
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setGenerationStatus("Preparing story generation...");

    try {
      const token = useAuthStore.getState().token;
      
      // Prepare story data
      const storyData = {
        title: storyTitle || "Untitled Story",
        globalEnvironment,
        globalStyle,
        narration: {
          enabled: enableNarration,
          useNativeSpeech: useNativeSpeech, // NEW: Use H3's built-in speech instead of TTS
          voice: narratorVoice,
          style: narratorStyle,
          speed: narratorSpeed,
        },
        backgroundMusic: {
          enabled: enableBackgroundMusic,
          mood: musicMood,
          volume: musicVolume,
        },
        aspectRatio: aspectRatio,
        characters: characters.map((c) => ({
          id: c.id,
          name: c.name,
          description: c.description,
          referenceImageUrl: c.referenceImageUrl,
        })),
        scenes: scenes.map((s, index) => ({
          id: s.id,
          index,
          title: s.title,
          narrative: s.narrative,
          visualPrompt: s.visualPrompt,
          duration: s.duration,
          environment: s.environment || globalEnvironment,
          characterIds: s.characterIds,
        })),
      };

      // Start generation
      const response = await fetch(`${BASE_URL}/api/story/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(storyData),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || "Generation failed");
      }
      
      if (result.videoId) {
        // Poll for progress
        pollGenerationProgress(result.videoId);
      } else {
        throw new Error("No video ID returned");
      }
    } catch (error) {
      console.error("Story generation error:", error);
      toast.error(error.message || "Failed to start story generation");
      setIsGenerating(false);
    }
  };

  // Poll generation progress
  const pollGenerationProgress = async (videoId) => {
    const token = useAuthStore.getState().token;
    
    const checkProgress = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/videos/${videoId}/progress`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        
        if (!response.ok) throw new Error("Failed to check progress");
        
        const data = await response.json();
        
        setGenerationProgress(data.progress_percent || 0);
        setGenerationStatus(data.progress || "Processing...");
        
        if (data.current_scene !== undefined) {
          setCurrentGeneratingScene(data.current_scene);
        }

        if (data.status === "completed" || data.status === "pending") {
          setIsGenerating(false);
          toast.success("Story video created successfully!");
          
          // Navigate directly to media library to show the video
          // Using direct navigate to avoid closure issues with long polling
          navigate(`/media-library?type=video&highlight=${videoId}`);
          
          // Also call the callback if provided (for any additional handling)
          if (onVideoCreated) {
            onVideoCreated(videoId);
          }
          return;
        }

        if (data.status === "failed") {
          setIsGenerating(false);
          toast.error(`Generation failed: ${data.error || "Unknown error"}`);
          return;
        }

        // Continue polling
        setTimeout(checkProgress, 3000);
      } catch (error) {
        console.error("Progress check error:", error);
        setTimeout(checkProgress, 5000);
      }
    };

    checkProgress();
  };

  // ===== RENDER =====
  return (
    <div className="space-y-6">
      {/* Header with tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#10B981]" />
          <span className="text-lg font-semibold text-white">Story Mode</span>
          <Badge variant="outline" className="text-[#10B981] border-[#10B981]/30">
            Beta
          </Badge>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-secondary/30 rounded-lg p-1">
        {[
          { id: "story", label: "Write Story", icon: BookOpen },
          { id: "characters", label: `Characters (${characters.length}/${MAX_CHARACTERS})`, icon: Users },
          { id: "scenes", label: `Scenes (${scenes.length}/${MAX_SCENES})`, icon: Film },
          { id: "generate", label: "Generate", icon: Sparkles },
          { id: "library", label: "Previous", icon: History },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id !== "library") setEditingStory(null);
            }}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-all flex-1 justify-center ${
              activeTab === tab.id
                ? "bg-[#10B981] text-white"
                : "text-muted-foreground hover:text-white hover:bg-secondary/50"
            }`}
            data-testid={`story-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {/* ===== STORY TAB ===== */}
        {activeTab === "story" && (
          <motion.div
            key="story"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label className="text-white">Story Title (optional)</Label>
              <Input
                placeholder="My Amazing Story"
                value={storyTitle}
                onChange={(e) => setStoryTitle(e.target.value)}
                className="bg-secondary/50 border-border/50"
                data-testid="story-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-[#10B981]" />
                Write or Paste Your Story
              </Label>
              <Textarea
                placeholder="Write your story here. You can write in narrative form - our AI will automatically:

• Split it into scenes
• Identify characters
• Convert descriptions to visual prompts

Example:
'Sarah, a young woman with red hair, walked through the bustling city streets. She stopped at a coffee shop where she met her friend Mike, a tall man with glasses. They sat by the window, laughing about old memories as the sun set outside...'

Tips:
- Describe character appearances when they first appear
- Include setting/environment details
- Write action and emotion, not just dialogue"
                value={storyNarrative}
                onChange={(e) => setStoryNarrative(e.target.value)}
                className="min-h-[300px] bg-secondary/50 border-border/50"
                data-testid="story-narrative-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleParseStory}
                disabled={!storyNarrative.trim() || isParsingStory}
                className="flex-1 bg-[#10B981] hover:bg-[#10B981]/90"
                data-testid="parse-story-btn"
              >
                {isParsingStory ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing Story...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Parse Story with AI
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setActiveTab("scenes")}
                className="border-border/50"
              >
                Skip to Manual Scenes
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Visual Style */}
            <div className="space-y-2 pt-4 border-t border-border/30">
              <Label className="text-white text-sm">Visual Style</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "cinematic", label: "Cinematic", desc: "Film-like quality" },
                  { id: "anime", label: "Anime", desc: "Japanese animation" },
                  { id: "realistic", label: "Realistic", desc: "Photo-realistic" },
                ].map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setGlobalStyle(style.id)}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      globalStyle === style.id
                        ? "border-[#10B981] bg-[#10B981]/10"
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    }`}
                    data-testid={`style-${style.id}`}
                  >
                    <p className="font-medium text-white text-sm">{style.label}</p>
                    <p className="text-xs text-muted-foreground">{style.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Video Format / Aspect Ratio */}
            <div className="space-y-2 pt-4 border-t border-border/30">
              <Label className="text-white text-sm">Video Format</Label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { id: "9:16", label: "9:16", platform: "TikTok", icon: "portrait" },
                  { id: "16:9", label: "16:9", platform: "YouTube", icon: "landscape" },
                  { id: "1:1", label: "1:1", platform: "Square", icon: "square" },
                  { id: "4:5", label: "4:5", platform: "IG Feed", icon: "portrait-short" },
                ].map((format) => (
                  <button
                    key={format.id}
                    onClick={() => setAspectRatio(format.id)}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      aspectRatio === format.id
                        ? "border-[#A855F7] bg-[#A855F7]/10"
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    }`}
                    data-testid={`story-aspect-ratio-${format.id.replace(":", "-")}`}
                    title={format.platform}
                  >
                    <div className={`mx-auto mb-1 border-2 border-current rounded-sm ${
                      format.icon === "portrait" ? "w-3 h-5" :
                      format.icon === "landscape" ? "w-5 h-3" :
                      format.icon === "square" ? "w-4 h-4" :
                      "w-3 h-4"
                    }`} />
                    <span className="text-xs font-medium text-white">{format.label}</span>
                    <p className="text-[10px] text-muted-foreground">{format.platform}</p>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* ===== CHARACTERS TAB ===== */}
        {activeTab === "characters" && (
          <motion.div
            key="characters"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Define your characters once - they'll stay consistent across all scenes
              </p>
              <Button
                size="sm"
                onClick={addCharacter}
                disabled={characters.length >= MAX_CHARACTERS}
                className="bg-[#10B981] hover:bg-[#10B981]/90"
                data-testid="add-character-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Character
              </Button>
            </div>

            {characters.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No characters yet</p>
                <p className="text-sm">Add characters to maintain consistency across scenes</p>
              </div>
            ) : (
              <div className="space-y-4">
                {characters.map((character, index) => (
                  <div
                    key={character.id}
                    className="p-4 rounded-lg border border-border/50 bg-secondary/20"
                    style={{ borderLeftColor: character.color, borderLeftWidth: 4 }}
                  >
                    <div className="flex items-start gap-4">
                      {/* Character Image */}
                      <div className="relative w-20 h-20 flex-shrink-0">
                        {character.referenceImagePreview ? (
                          <img
                            src={character.referenceImagePreview}
                            alt={character.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-full rounded-lg bg-secondary/50 flex items-center justify-center">
                            <User className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                        {uploadingCharacterId === character.id && (
                          <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-white" />
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={(el) => (characterImageRefs.current[character.id] = el)}
                          onChange={(e) => handleCharacterImageUpload(character.id, e)}
                        />
                      </div>

                      {/* Character Details */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2">
                          <Input
                            value={character.name}
                            onChange={(e) => updateCharacter(character.id, { name: e.target.value })}
                            placeholder="Character name"
                            className="bg-secondary/50 border-border/50 font-medium"
                            data-testid={`character-name-${index}`}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => removeCharacter(character.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Textarea
                          value={character.description}
                          onChange={(e) => updateCharacter(character.id, { description: e.target.value })}
                          placeholder="Describe appearance: age, hair color, clothing, distinctive features..."
                          className="bg-secondary/50 border-border/50 min-h-[80px]"
                          data-testid={`character-desc-${index}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => characterImageRefs.current[character.id]?.click()}
                            disabled={uploadingCharacterId === character.id}
                            className="border-border/50"
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            Upload Image
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => generateCharacterReference(character.id)}
                            disabled={uploadingCharacterId === character.id || !character.description.trim()}
                            className="border-[#10B981]/50 text-[#10B981] hover:bg-[#10B981]/10"
                          >
                            <Wand2 className="w-3 h-3 mr-1" />
                            Auto-Generate
                          </Button>
                        </div>
                        {character.isAutoGenerated && (
                          <p className="text-xs text-muted-foreground">
                            AI-generated reference image
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button onClick={() => setActiveTab("scenes")} className="bg-[#10B981]">
                Continue to Scenes
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* ===== SCENES TAB ===== */}
        {activeTab === "scenes" && (
          <motion.div
            key="scenes"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Arrange and customize your story scenes
              </p>
              <Button
                size="sm"
                onClick={addScene}
                disabled={scenes.length >= MAX_SCENES}
                className="bg-[#10B981] hover:bg-[#10B981]/90"
                data-testid="add-scene-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Scene
              </Button>
            </div>

            {/* Global Environment */}
            <div className="p-3 rounded-lg bg-secondary/20 border border-border/30">
              <div className="flex items-center gap-2 mb-2">
                <Palette className="w-4 h-4 text-[#10B981]" />
                <Label className="text-sm text-white">Default Environment (applies to all scenes)</Label>
              </div>
              <Input
                value={globalEnvironment}
                onChange={(e) => setGlobalEnvironment(e.target.value)}
                placeholder="e.g., Modern city streets, sunny afternoon, bustling crowds"
                className="bg-secondary/50 border-border/50"
                data-testid="global-environment-input"
              />
            </div>

            {/* Scene List */}
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {scenes.map((scene, index) => (
                  <div
                    key={scene.id}
                    className={`rounded-lg border transition-all ${
                      expandedSceneId === scene.id
                        ? "border-[#10B981] bg-[#10B981]/5"
                        : "border-border/50 bg-secondary/20"
                    }`}
                  >
                    {/* Scene Header */}
                    <div
                      className="p-3 cursor-pointer flex items-center justify-between"
                      onClick={() => setExpandedSceneId(expandedSceneId === scene.id ? null : scene.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); moveScene(index, "up"); }}
                            disabled={index === 0}
                            className="p-0.5 hover:bg-secondary/50 rounded disabled:opacity-30"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); moveScene(index, "down"); }}
                            disabled={index === scenes.length - 1}
                            className="p-0.5 hover:bg-secondary/50 rounded disabled:opacity-30"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </div>
                        <Badge variant="outline" className="text-[#10B981] border-[#10B981]/30">
                          {index + 1}
                        </Badge>
                        <span className="font-medium text-white">{scene.title}</span>
                        <span className="text-xs text-muted-foreground">{scene.duration}s</span>
                        {scene.characterIds.length > 0 && (
                          <div className="flex -space-x-1">
                            {scene.characterIds.slice(0, 3).map((charId) => {
                              const char = characters.find((c) => c.id === charId);
                              return char ? (
                                <div
                                  key={charId}
                                  className="w-5 h-5 rounded-full border-2 border-background"
                                  style={{ backgroundColor: char.color }}
                                  title={char.name}
                                />
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {scene.status === "complete" && (
                          <Check className="w-4 h-4 text-green-500" />
                        )}
                        {scene.status === "generating" && (
                          <Loader2 className="w-4 h-4 animate-spin text-[#10B981]" />
                        )}
                        {scene.status === "error" && (
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); removeScene(scene.id); }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            expandedSceneId === scene.id ? "rotate-180" : ""
                          }`}
                        />
                      </div>
                    </div>

                    {/* Scene Expanded Content */}
                    <AnimatePresence>
                      {expandedSceneId === scene.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 pt-0 space-y-4 border-t border-border/30">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-sm text-white">Scene Title</Label>
                                <Input
                                  value={scene.title}
                                  onChange={(e) => updateScene(scene.id, { title: e.target.value })}
                                  className="bg-secondary/50 border-border/50"
                                  data-testid={`scene-title-${index}`}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm text-white">Duration (seconds)</Label>
                                <Input
                                  type="number"
                                  min={3}
                                  max={15}
                                  value={scene.duration}
                                  onChange={(e) => updateScene(scene.id, { duration: parseInt(e.target.value) || 5 })}
                                  className="bg-secondary/50 border-border/50"
                                  data-testid={`scene-duration-${index}`}
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label className="text-sm text-white">Narrative / What happens</Label>
                              <Textarea
                                value={scene.narrative}
                                onChange={(e) => updateScene(scene.id, { narrative: e.target.value })}
                                placeholder="Describe what happens in this scene..."
                                className="bg-secondary/50 border-border/50 min-h-[80px]"
                                data-testid={`scene-narrative-${index}`}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="text-sm text-white">Visual Prompt (optional - AI can generate)</Label>
                              <Textarea
                                value={scene.visualPrompt}
                                onChange={(e) => updateScene(scene.id, { visualPrompt: e.target.value })}
                                placeholder="Specific visual description for video generation..."
                                className="bg-secondary/50 border-border/50 min-h-[60px]"
                                data-testid={`scene-visual-${index}`}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="text-sm text-white">Environment (override global)</Label>
                              <Input
                                value={scene.environment}
                                onChange={(e) => updateScene(scene.id, { environment: e.target.value })}
                                placeholder={globalEnvironment || "Same as global environment"}
                                className="bg-secondary/50 border-border/50"
                                data-testid={`scene-environment-${index}`}
                              />
                            </div>

                            {/* Characters in Scene */}
                            {characters.length > 0 && (
                              <div className="space-y-2">
                                <Label className="text-sm text-white">Characters in this scene</Label>
                                <div className="flex flex-wrap gap-2">
                                  {characters.map((char) => (
                                    <button
                                      key={char.id}
                                      onClick={() => toggleCharacterInScene(scene.id, char.id)}
                                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                                        scene.characterIds.includes(char.id)
                                          ? "bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/50"
                                          : "bg-secondary/30 text-muted-foreground border border-border/50 hover:border-border"
                                      }`}
                                    >
                                      <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: char.color }}
                                      />
                                      {char.name}
                                      {scene.characterIds.includes(char.id) && (
                                        <Check className="w-3 h-3" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex justify-end pt-4">
              <Button onClick={() => setActiveTab("generate")} className="bg-[#10B981]">
                Review & Generate
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* ===== GENERATE TAB ===== */}
        {activeTab === "generate" && (
          <motion.div
            key="generate"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Summary */}
            <div className="p-4 rounded-lg bg-secondary/20 border border-border/30 space-y-4">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Film className="w-5 h-5 text-[#10B981]" />
                Story Summary
              </h3>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded-lg bg-secondary/30">
                  <p className="text-2xl font-bold text-[#10B981]">{scenes.length}</p>
                  <p className="text-xs text-muted-foreground">Scenes</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/30">
                  <p className="text-2xl font-bold text-[#10B981]">{characters.length}</p>
                  <p className="text-xs text-muted-foreground">Characters</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/30">
                  <p className="text-2xl font-bold text-[#10B981]">
                    {scenes.reduce((acc, s) => acc + s.duration, 0)}s
                  </p>
                  <p className="text-xs text-muted-foreground">Total Duration</p>
                </div>
              </div>

              {storyTitle && (
                <div>
                  <p className="text-sm text-muted-foreground">Title</p>
                  <p className="text-white font-medium">{storyTitle}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-muted-foreground">Visual Style</p>
                <p className="text-white capitalize">{globalStyle}</p>
              </div>

              {/* Scene preview list */}
              <div>
                <p className="text-sm text-muted-foreground mb-2">Scene Order</p>
                <div className="flex flex-wrap gap-2">
                  {scenes.map((scene, index) => (
                    <Badge
                      key={scene.id}
                      variant="outline"
                      className="text-xs"
                    >
                      {index + 1}. {scene.title} ({scene.duration}s)
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* ===== NARRATION SETTINGS ===== */}
            <div className="p-4 rounded-lg bg-secondary/20 border border-border/30 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Mic className="w-5 h-5 text-[#A855F7]" />
                  AI Narration
                </h3>
                <button
                  onClick={() => setEnableNarration(!enableNarration)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    enableNarration ? "bg-[#A855F7]" : "bg-secondary/50"
                  }`}
                  data-testid="narration-toggle"
                >
                  <span
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      enableNarration ? "left-7" : "left-1"
                    }`}
                  />
                </button>
              </div>

              {enableNarration && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  {/* Native Speech vs TTS Toggle - NEW */}
                  <div className="p-3 rounded-lg bg-secondary/30 border border-border/50 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🗣️</span>
                        <div>
                          <p className="text-sm font-medium text-white">Native AI Speech</p>
                          <p className="text-xs text-muted-foreground">
                            Characters speak naturally (H3 Turbo built-in speech)
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setUseNativeSpeech(!useNativeSpeech)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          useNativeSpeech ? "bg-[#10B981]" : "bg-secondary/50"
                        }`}
                        data-testid="native-speech-toggle"
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                            useNativeSpeech ? "left-7" : "left-1"
                          }`}
                        />
                      </button>
                    </div>
                    {useNativeSpeech ? (
                      <p className="text-xs text-[#10B981] bg-[#10B981]/10 p-2 rounded">
                        ✓ Characters will speak the dialogue naturally. Each scene's narrative text will be their spoken lines.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground bg-secondary/30 p-2 rounded">
                        🎙️ A separate AI narrator will read the story over the video (TTS voiceover).
                      </p>
                    )}
                  </div>

                  {/* Only show TTS voice options when NOT using native speech */}
                  {!useNativeSpeech && (
                    <>
                      <p className="text-sm text-muted-foreground">
                        AI will narrate your story based on each scene's narrative text
                      </p>

                  {/* Voice Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm text-white">Narrator Voice</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: "female", label: "Female", icon: "👩" },
                        { id: "male", label: "Male", icon: "👨" },
                        { id: "auto", label: "Auto", icon: "✨" },
                      ].map((voice) => (
                        <button
                          key={voice.id}
                          onClick={() => setNarratorVoice(voice.id)}
                          className={`p-2 rounded-lg border-2 transition-all text-center ${
                            narratorVoice === voice.id
                              ? "border-[#A855F7] bg-[#A855F7]/10"
                              : "border-border/50 bg-secondary/30 hover:border-border"
                          }`}
                          data-testid={`narrator-voice-${voice.id}`}
                        >
                          <span className="text-lg">{voice.icon}</span>
                          <p className="text-xs text-white mt-1">{voice.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Narration Style */}
                  <div className="space-y-2">
                    <Label className="text-sm text-white">Narration Style</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "storyteller", label: "Storyteller", desc: "Warm & engaging" },
                        { id: "dramatic", label: "Dramatic", desc: "Intense & emotional" },
                        { id: "calm", label: "Calm", desc: "Soothing & relaxed" },
                        { id: "energetic", label: "Energetic", desc: "Upbeat & exciting" },
                      ].map((style) => (
                        <button
                          key={style.id}
                          onClick={() => setNarratorStyle(style.id)}
                          className={`p-2 rounded-lg border-2 transition-all text-left ${
                            narratorStyle === style.id
                              ? "border-[#A855F7] bg-[#A855F7]/10"
                              : "border-border/50 bg-secondary/30 hover:border-border"
                          }`}
                          data-testid={`narrator-style-${style.id}`}
                        >
                          <p className="text-sm text-white font-medium">{style.label}</p>
                          <p className="text-xs text-muted-foreground">{style.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Narration Speed */}
                  <div className="space-y-2">
                    <Label className="text-sm text-white">Narration Speed</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: "slow", label: "Slow", desc: "0.85x" },
                        { id: "normal", label: "Normal", desc: "1.0x" },
                        { id: "fast", label: "Fast", desc: "1.15x" },
                      ].map((speed) => (
                        <button
                          key={speed.id}
                          onClick={() => setNarratorSpeed(speed.id)}
                          className={`p-2 rounded-lg border-2 transition-all text-center ${
                            narratorSpeed === speed.id
                              ? "border-[#A855F7] bg-[#A855F7]/10"
                              : "border-border/50 bg-secondary/30 hover:border-border"
                          }`}
                          data-testid={`narrator-speed-${speed.id}`}
                        >
                          <p className="text-sm text-white font-medium">{speed.label}</p>
                          <p className="text-xs text-muted-foreground">{speed.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                    </>
                  )}
                </motion.div>
              )}

              {!enableNarration && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <VolumeX className="w-4 h-4" />
                  Video will be generated without narration
                </p>
              )}
            </div>

            {/* ===== BACKGROUND MUSIC SETTINGS ===== */}
            <div className="p-4 rounded-lg bg-secondary/20 border border-border/30 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Volume2 className="w-5 h-5 text-[#F59E0B]" />
                  Background Music
                </h3>
                <button
                  onClick={() => setEnableBackgroundMusic(!enableBackgroundMusic)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    enableBackgroundMusic ? "bg-[#F59E0B]" : "bg-secondary/50"
                  }`}
                  data-testid="music-toggle"
                >
                  <span
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      enableBackgroundMusic ? "left-7" : "left-1"
                    }`}
                  />
                </button>
              </div>

              {enableBackgroundMusic && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  <p className="text-sm text-muted-foreground">
                    Add ambient music to enhance your story's mood
                  </p>

                  {/* Music Mood Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm text-white">Music Mood</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: "cinematic", label: "Cinematic", icon: "🎬", desc: "Epic & dramatic" },
                        { id: "uplifting", label: "Uplifting", icon: "✨", desc: "Positive & inspiring" },
                        { id: "suspenseful", label: "Suspenseful", icon: "🎭", desc: "Tense & mysterious" },
                        { id: "romantic", label: "Romantic", icon: "💕", desc: "Soft & emotional" },
                        { id: "adventure", label: "Adventure", icon: "🗺️", desc: "Exciting & bold" },
                        { id: "calm", label: "Calm", icon: "🌊", desc: "Peaceful & serene" },
                      ].map((mood) => (
                        <button
                          key={mood.id}
                          onClick={() => setMusicMood(mood.id)}
                          className={`p-2 rounded-lg border-2 transition-all text-left ${
                            musicMood === mood.id
                              ? "border-[#F59E0B] bg-[#F59E0B]/10"
                              : "border-border/50 bg-secondary/30 hover:border-border"
                          }`}
                          data-testid={`music-mood-${mood.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{mood.icon}</span>
                            <div>
                              <p className="text-sm text-white font-medium">{mood.label}</p>
                              <p className="text-xs text-muted-foreground">{mood.desc}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Music Volume */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-white">Music Volume</Label>
                      <span className="text-sm text-[#F59E0B]">{musicVolume}%</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <VolumeX className="w-4 h-4 text-muted-foreground" />
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={musicVolume}
                        onChange={(e) => setMusicVolume(parseInt(e.target.value))}
                        className="flex-1 h-2 rounded-lg appearance-none cursor-pointer bg-secondary/50 accent-[#F59E0B]"
                        data-testid="music-volume-slider"
                      />
                      <Volume2 className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Tip: Keep volume low (20-40%) so narration remains clear
                    </p>
                  </div>
                </motion.div>
              )}

              {!enableBackgroundMusic && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <VolumeX className="w-4 h-4" />
                  Video will be generated without background music
                </p>
              )}
            </div>

            {/* Generation Progress */}
            {isGenerating && (
              <div className="p-4 rounded-lg bg-[#10B981]/10 border border-[#10B981]/30 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[#10B981] font-medium">{generationStatus}</span>
                  <span className="text-[#10B981]">{generationProgress}%</span>
                </div>
                <Progress value={generationProgress} className="h-2" />
                {currentGeneratingScene !== null && (
                  <p className="text-sm text-muted-foreground">
                    Generating scene {currentGeneratingScene + 1} of {scenes.length}...
                  </p>
                )}
              </div>
            )}

            {/* Warnings */}
            {characters.length === 0 && (
              <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-500 font-medium">No characters defined</p>
                  <p className="text-sm text-muted-foreground">
                    Without character references, AI will generate different people in each scene.
                    <button
                      onClick={() => setActiveTab("characters")}
                      className="text-[#10B981] hover:underline ml-1"
                    >
                      Add characters
                    </button>
                  </p>
                </div>
              </div>
            )}

            {scenes.some((s) => !s.narrative.trim() && !s.visualPrompt.trim()) && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-500 font-medium">Empty scenes detected</p>
                  <p className="text-sm text-muted-foreground">
                    All scenes need content.
                    <button
                      onClick={() => setActiveTab("scenes")}
                      className="text-[#10B981] hover:underline ml-1"
                    >
                      Edit scenes
                    </button>
                  </p>
                </div>
              </div>
            )}

            {/* Generate Button */}
            <Button
              onClick={handleGenerateStory}
              disabled={
                isGenerating ||
                scenes.length === 0 ||
                scenes.some((s) => !s.narrative.trim() && !s.visualPrompt.trim())
              }
              className="w-full h-14 text-lg bg-gradient-to-r from-[#10B981] to-[#059669] hover:opacity-90"
              data-testid="generate-story-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating Story Video...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Story Video ({scenes.length} scenes)
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Estimated time: {scenes.length * 2}-{scenes.length * 4} minutes
            </p>
          </motion.div>
        )}

        {/* ===== LIBRARY TAB ===== */}
        {activeTab === "library" && (
          <motion.div
            key="library"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {editingStory ? (
              <StoryEditor
                story={editingStory}
                onClose={() => setEditingStory(null)}
                onRefresh={() => {
                  // Refresh library when a scene is regenerated
                  setEditingStory(null);
                  setTimeout(() => setActiveTab("library"), 100);
                }}
              />
            ) : (
              <StoryLibraryPanel
                onEditStory={(story) => setEditingStory(story)}
                onSelectStory={(story) => {
                  // Optionally load story into editor
                  setEditingStory(story);
                }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
